import { FC, useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { getAddressSearch } from "tm-shared/api";
import { Option } from "tm-shared/types";
import { validationMessage } from "tm-shared/utils";

import { useAsyncOptions } from "@/hooks";
import { useAppDispatch } from "@/hooks/redux";
import { RHFAutocomplete } from "@/shared/forms/RHF/RHFAutocomplete";
import { setAddressVerifyDuplicateResolution } from "@/store/slices/ui";
import { AddressVerifyFormValues } from "@/types/form";

interface AddressSectionProps {
    regionId: string;
    address?: string;
    options: Option[];
    setOptions: (options: Option[]) => void;
    onOpenAddAddressModal: () => void;
}

export const AddressSection: FC<AddressSectionProps> = ({
    onOpenAddAddressModal,
    regionId,
    address,
    options,
    setOptions,
}) => {
    const dispatch = useAppDispatch();

    const { setValue } = useFormContext<AddressVerifyFormValues>();

    const handleOnAfterChangeAddress = useCallback(() => {
        dispatch(setAddressVerifyDuplicateResolution(null));
    }, [dispatch]);

    const [searchQuery, setSearchQuery] = useState("");

    const { options: fetchedOptions, loading: loadingAddress } =
        useAsyncOptions({
            fetchFn: (signal) => {
                return getAddressSearch(
                    {
                        name: searchQuery,
                        areaId: regionId,
                    },
                    {
                        axiosOverrides: { signal },
                    },
                );
            },
            selectData: (response) => Object.entries(response.result),
            mapToOption: ([value, label]) => ({ label, value }),
            dependencies: [searchQuery],
            enabled: (!searchQuery && !address) || searchQuery.length >= 3,
            debounceMs: 500,
        });

    useEffect(() => {
        if (fetchedOptions?.length) {
            setOptions(fetchedOptions);
        }
    }, [fetchedOptions, setOptions]);

    const handleInputChange = useCallback(
        (value: string) => {
            setSearchQuery(value);
            setValue("address", "");
            dispatch(setAddressVerifyDuplicateResolution(null));
        },
        [dispatch, setValue],
    );

    return (
        <RHFAutocomplete
            name="address"
            options={options ?? []}
            label="Адреса"
            rules={{
                required: validationMessage.required,
            }}
            actionOption={{
                label: "Додати адресу",
                onClick: onOpenAddAddressModal,
            }}
            allowClear={false}
            loading={loadingAddress}
            onInputChange={handleInputChange}
            selectProps={{
                size: "large",
            }}
            onAfterChange={handleOnAfterChangeAddress}
        />
    );
};
